import { MdCheck, MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Modal from "../Modal/Modal";
import ModalFooter from "../Modal/ModalFooter";
import TextInput from "../TextInput/TextInput";
import { useMemo, useState } from "react";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { showNotification } from "src/helpers/utils";
import apiClient from "../../helpers/api-client";
import PasswordGenerator from "../PasswordGenerator/PasswordGenerator";
import RadioGroup from "../RadioGroup/RadioGroup";
import InfoBlock from "../InfoBlock/InfoBlock";
import { API_ENDPOINT_UPDATE_PROFILE } from "src/helpers/endpoint-urls";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";
import { EInfoBlockKind } from "../InfoBlock/InfoBlock.types";

const PASSWORD_MIN_LENGTH = 8;

interface IChangePasswordModal {
  onClose: () => void;
}

const ChangePasswordModal: React.FC<IChangePasswordModal> = ({ onClose }) => {
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");

  const [isUpdatingPassword, setIsUpdatingPassword] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [usePasswordGenerator, setUsePasswordGenerator] =
    useState<boolean>(true);

  const { t } = useTranslation(["modals", "common"]);

  const updatePassword = () => {
    setIsUpdatingPassword(true);

    const formData = new FormData();

    formData.append("password", newPassword?.trim?.());

    apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        // headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        showNotification({
          kind: "success",

          message: t("modals:change_password.notifications.success"),
        });
        setNewPassword("");
        setShowConfirmDialog(false);
      })
      .catch((e: any) => {
        showNotification({
          message: t("modals:change_password.notifications.error"),
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdatingPassword(false);
      });
  };

  const hasRequiredLength = useMemo(() => {
    if (!newPassword) return false;

    return `${newPassword}`.trim?.()?.length >= PASSWORD_MIN_LENGTH;
  }, [newPassword]);

  const passwordsMatch = useMemo(() => {
    if (newPassword.trim()) {
      return (
        confirmNewPassword.trim() &&
        confirmNewPassword.trim() === newPassword.trim()
      );
    }

    return false;
  }, [newPassword, confirmNewPassword]);

  const canChangePassword = useMemo(() => {
    return (
      currentPassword &&
      newPassword &&
      confirmNewPassword &&
      newPassword === confirmNewPassword &&
      hasRequiredLength
    );
  }, [currentPassword, newPassword, confirmNewPassword, hasRequiredLength]);

  const infoMessage = useMemo(() => {
    let message: string | any[] = [];
    let title = "";

    if (hasRequiredLength && passwordsMatch) {
      return null;
    }

    if (!hasRequiredLength) {
      title = "Longitud mínima";
      message.push(
        `La longitud mínima es de ${PASSWORD_MIN_LENGTH} caracteres.`,
      );
    }

    if (!passwordsMatch) {
      title = "No coinciden";
      message.push("Las contraseñas no coinciden.");
    }

    if (message.length === 2) {
      title = "Atención";
    }

    if (message.length === 1) {
      message = message[0];
    }

    return {
      message,
      title,
    };
  }, [passwordsMatch, hasRequiredLength]);

  return (
    <>
      <Modal
        onEscape={() => onClose()}
        className={clsx("md:!max-w-[500px]", "md:!w-full")}
      >
        <Modal.Header
          title={t("modals:change_password.title")}
          onClose={() => onClose()}
        />
        <Modal.Body>
          <div className={clsx(["flex", "flex-col", "gap-6", "w-full"])}>
            <TextInput
              value={currentPassword}
              onChange={(e: any) => setCurrentPassword(e?.target?.value)}
              label={t("modals:change_password.current_password_label")}
              placeholder={t(
                "modals:change_password.current_password_placeholder",
              )}
              type="password"
              required
              autoComplete="off"
            />
            <RadioGroup
              options={[
                {
                  value: "password-generator",
                  label: "Generar contraseña",
                  description:
                    "Generar automáticamente una contraseña más segura",
                },
                {
                  value: "custom-password",
                  label: "Contraseña personalizada",
                  description:
                    "Permitirme utilizar una contraseña personalizada aunque sea menos segura",
                },
              ]}
              columns={1}
              onChange={(value: any) => {
                if (value === "password-generator") {
                  setUsePasswordGenerator(true);
                } else {
                  setNewPassword("");
                  setConfirmNewPassword("");
                  setUsePasswordGenerator(false);
                }
              }}
              selected={
                usePasswordGenerator ? "password-generator" : "custom-password"
              }
            />
            {infoMessage && (
              <InfoBlock
                title={infoMessage.title}
                displayMobileIcon
                message={infoMessage.message}
                kind={EInfoBlockKind.WARNING}
              />
            )}
            {usePasswordGenerator ? (
              <PasswordGenerator
                onChange={(password: string) => {
                  setNewPassword(password);
                  setConfirmNewPassword(password);
                }}
              />
            ) : (
              <>
                <TextInput
                  value={newPassword}
                  onChange={(e: any) => setNewPassword(e?.target?.value)}
                  label={t("modals:change_password.new_password_label")}
                  placeholder={t(
                    "modals:change_password.new_password_placeholder",
                  )}
                  type="password"
                  required
                  autoComplete="off"
                />
                <TextInput
                  value={confirmNewPassword}
                  onChange={(e: any) => setConfirmNewPassword(e?.target?.value)}
                  label={t("modals:change_password.confirm_password_label")}
                  placeholder={t(
                    "modals:change_password.confirm_password_placeholder",
                  )}
                  type="password"
                  required
                  autoComplete="off"
                />
              </>
            )}
          </div>
        </Modal.Body>
        <ModalFooter
          actions={[
            {
              text: t("common:buttons.update"),
              onClick: () => {},
              icon: <MdCheck size={ICON_SIZES.SM} />,
              kind: EButtonKind.SAVE,
              isLoading: isUpdatingPassword,
              disabled: isUpdatingPassword || !canChangePassword,
            },
            {
              text: t("common:buttons.close"),
              icon: <MdClose size={ICON_SIZES.SM} />,
              onClick: () => onClose(),
              kind: EButtonKind.NORMAL,
            },
          ]}
        />
      </Modal>
      {showConfirmDialog && (
        <ConfirmDialog
          title={t("modals:change_password.confirm.title")}
          description={t("modals:change_password.confirm.description")}
          onClose={() => {
            setShowConfirmDialog(false);
          }}
          confirmKind={EButtonKind.SAVE}
          onConfirm={() => {
            updatePassword();
          }}
          isLoading={isUpdatingPassword}
        />
      )}
    </>
  );
};

export default ChangePasswordModal;
